import { useMemo, useState, Fragment, useEffect, useContext } from 'react';
import styled from 'styled-components';
import getIn from 'immutable-less/transmute/getIn';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import { DataSourceType } from 'automation-ui-formula-editor/data-passing-panel/types/dataSourcesTypes';
import { getDefaultOpenPropertyGroups, getGroupLabel, getGroupSortOrder, filterAndGroupPropertiesByType } from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/utils/dataSourceSingleDataSourceUtils';
import { isPropertyTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyTypeAllowListed';
import { isPropertyFieldTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyFieldTypeAllowListed';
import { useGetDataSourceFromToObjectId } from 'automation-ui-formula-editor/data-sources/hooks/useGetDataSourceFromToObjectId';
import DataPanelProperty from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelProperty/DataPanelProperty';
import DataPassingPanelBodyNestedDataSource from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPassingPanelBodyNestedDataSource';
import { ENROLLMENT_EVENTS } from 'automation-ui-formula-editor/data-sources/constants/dataSourcesConstants';
import { isDataSourceAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isDataSourceAllowListed';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledLabel = styled.div.withConfig({
  displayName: "DataPassingPanelBodySingleDataSource__StyledLabel",
  componentId: "sc-7r4mp7-0"
})(["position:relative;"]);
const DataPassingPanelBodySingleDataSource = ({
  dataSource,
  disabled,
  dataSourceAllowList,
  propertyFieldTypeAllowList,
  propertyTypeAllowList,
  requiredPermissionLevel,
  flowId,
  hideNestedDataSources = false,
  isEmbedded,
  onDataPropertySelected,
  searchFilterString,
  refetchDataSource
}) => {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const filteredAndGroupedPropertiesByType = useMemo(() => filterAndGroupPropertiesByType({
    dataSource,
    hideNestedDataSources,
    searchFilterString
  }), [dataSource, hideNestedDataSources, searchFilterString]);
  const [isGroupOpen, setIsGroupOpen] = useState(getDefaultOpenPropertyGroups({
    groupedPropertiesByType: filteredAndGroupedPropertiesByType,
    propertyTypeAllowList
  }));
  const dataSourceDefinition = getIn(['dataSourceDefinition'], dataSource);
  const dataSourceName = getIn(['dataSourceDefinition', 'name'], dataSource);
  const isDataSourceFilteredOut = !isDataSourceAllowListed({
    prefix: dataSourceName,
    dataSourceAllowList
  });
  const {
    toObjectTypeId
  } = useGetDataSourceFromToObjectId({
    prefix: dataSourceName
  });
  const hideEditPropertyLink = isEmbedded || dataSourceName.startsWith(ENROLLMENT_EVENTS);
  useEffect(() => {
    if (!dataSourceDefinition) {
      return;
    }
    trackEvent(usageTracker, Trackables.VIEWED_DATA_PANEL_DATA_SOURCE, {
      screen: 'Data Passing Panel UX Revised',
      flowId,
      source: dataSourceDefinition.name === 'enrolled_object' ? 'ENROLLED_OBJECT' : dataSourceDefinition.dataSourceType
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);
  const handleOnOpenChange = (key, open) => {
    const newIsGroupOpen = new Map(isGroupOpen);
    newIsGroupOpen.set(key, open);
    setIsGroupOpen(newIsGroupOpen);
  };
  const renderAccordionContent = properties => {
    if (properties.length) {
      return properties.sort((prevProp, currProp) => prevProp.label.toLowerCase().localeCompare(currProp.label.toLowerCase())).map(property => {
        const {
          name,
          connection
        } = property;
        const renderProperty = () => /*#__PURE__*/_jsx(DataPanelProperty, {
          onDataPropertySelected: () => onDataPropertySelected(property, dataSourceDefinition, `${dataSourceName}.${property.name}`),
          disabled: disabled,
          fromDataSourceName: dataSourceName,
          dataProperty: property,
          isPropertyTypeCompatible: isPropertyTypeAllowListed({
            property,
            propertyTypeAllowList
          }) && isPropertyFieldTypeAllowListed({
            property,
            propertyFieldTypeAllowList,
            dataSourceType: dataSourceDefinition.dataSourceType
          }),
          requiredPermissionLevel: requiredPermissionLevel,
          objectTypeId: toObjectTypeId,
          refetchDataSource: refetchDataSource,
          hideEditPropertyLink: hideEditPropertyLink,
          isDataSourceFilteredOut: isDataSourceFilteredOut
        }, name);
        const renderNestedDataSource = () => {
          if (!connection) {
            return null;
          }
          const {
            toDataSource
          } = connection;
          return /*#__PURE__*/_jsx(DataPassingPanelBodyNestedDataSource, {
            onDataPropertySelected: onDataPropertySelected,
            disabled: disabled,
            dataSourceToView: {
              prefix: toDataSource
            },
            dataSourceAllowList: dataSourceAllowList,
            propertyTypeAllowList: propertyTypeAllowList,
            requiredPermissionLevel: requiredPermissionLevel,
            searchFilterString: searchFilterString
          });
        };
        if (!hideNestedDataSources && property.dataPropertyType === DataSourceType.CONNECTION) {
          return /*#__PURE__*/_jsxs(Fragment, {
            children: [renderProperty(), renderNestedDataSource()]
          }, `nested-property-fragment-${name}`);
        }
        return renderProperty();
      });
    }

    // Empty state if there are no items
    return /*#__PURE__*/_jsx(DataPanelProperty, {
      onDataPropertySelected: () => {},
      fromDataSourceName: '',
      disabled: true // keep disabled
      ,
      dataProperty: {
        name: '',
        label: searchFilterString ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-filtered-properties-found"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-properties"
        }),
        description: '',
        dataPropertyType: DataSourceType.EMPTY
      },
      isPropertyTypeCompatible: true,
      requiredPermissionLevel: "none"
    }, 'no_');
  };
  if (filteredAndGroupedPropertiesByType.size) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: Array.from(filteredAndGroupedPropertiesByType).sort(([keyA], [keyB]) => {
        const keyAOrder = getGroupSortOrder(keyA);
        const keyBOrder = getGroupSortOrder(keyB);
        if (keyAOrder < keyBOrder) {
          return -1;
        }
        if (keyAOrder > keyBOrder) {
          return 1;
        }
        return 0;
      }).map(([key, rawProperties]) => {
        const properties = rawProperties;
        return /*#__PURE__*/_jsx(UIAccordionItem, {
          "data-test-id": `data-panel-property-group-${key}`,
          title: /*#__PURE__*/_jsx(StyledLabel, {
            children: getGroupLabel(key)
          }),
          size: 'small',
          open: !!isGroupOpen.get(key) || !!(searchFilterString && properties.length),
          className: "p-x-3",
          onOpenChange: event => handleOnOpenChange(key, event.target.value),
          renderContent: () => renderAccordionContent(properties)
        }, key);
      })
    });
  }

  // no properties or groups, display an empty state
  return /*#__PURE__*/_jsx("div", {
    className: "p-left-3",
    children: /*#__PURE__*/_jsx(DataPanelProperty, {
      onDataPropertySelected: () => {},
      fromDataSourceName: '',
      disabled: true // keep disabled
      ,
      dataProperty: {
        name: '',
        label: searchFilterString ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-filtered-properties-found"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-properties"
        }),
        description: '',
        dataPropertyType: DataSourceType.EMPTY
      },
      isPropertyTypeCompatible: true,
      requiredPermissionLevel: "none"
    }, 'no_')
  });
};
export default DataPassingPanelBodySingleDataSource;